import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import { CharlieDialog, CharlieDialogContent } from '@ver.id/libs.ui.charlie';
import { countries } from '@ver.id/libs.ui.yondu';
import { useTranslation } from '../../i18n/i18n';

/** ISO 3166-1 alpha-2 codes relevant for the app picker filter. */
const FILTER_CODES = new Set([
  'NL',
  'DE',
  'FR',
  'GB',
  'US',
  'AU',
  'CA',
  'IE',
  'NZ',
]);

const COUNTRY_OPTIONS = countries.filter((c) => FILTER_CODES.has(c.value));

type Props = {
  open: boolean;
  selected: string[];
  onChange: (countries: string[]) => void;
  onClose: () => void;
};

export default function HostedCountryFilterModal({
  open,
  selected,
  onChange,
  onClose,
}: Props) {
  const { t } = useTranslation();

  if (!open) return null;

  const toggle = (code: string) => {
    if (selected.includes(code)) {
      onChange(selected.filter((c) => c !== code));
    } else {
      onChange([...selected, code]);
    }
  };

  const clearAll = () => onChange([]);

  return (
    <CharlieDialog
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <CharlieDialogContent hideClose className="max-w-sm p-0">
        {/* Header */}
        <div className="flex items-center justify-between px-4 pt-4 pb-2">
          <h3 className="text-base font-semibold text-foreground">
            {t('appPicker.filterTitle')}
          </h3>
        </div>

        {/* Country list */}
        <div className="px-4 pb-2 max-h-64 overflow-y-auto">
          {COUNTRY_OPTIONS.map(({ value: code, label }) => {
            const isSelected = selected.includes(code);
            return (
              <button
                key={code}
                type="button"
                onClick={() => toggle(code)}
                className="flex items-center gap-3 w-full rounded-md px-2 py-2 text-left text-sm transition-colors hover:bg-accent"
              >
                <div
                  className={`flex items-center justify-center size-4 rounded border ${
                    isSelected
                      ? 'bg-foreground border-foreground'
                      : 'border-input'
                  }`}
                >
                  {isSelected && (
                    <FontAwesomeIcon
                      icon={faCheck}
                      className="size-3 text-background"
                    />
                  )}
                </div>
                <span className="text-foreground">{label}</span>
                <span className="text-muted-foreground ml-auto text-xs">
                  {code}
                </span>
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-border">
          <button
            type="button"
            onClick={clearAll}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            disabled={selected.length === 0}
          >
            {t('appPicker.filterClear')}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="text-sm font-medium text-foreground bg-accent rounded-md px-3 py-1.5 hover:bg-accent/80 transition-colors"
          >
            {t('appPicker.filterDone')}
          </button>
        </div>
      </CharlieDialogContent>
    </CharlieDialog>
  );
}
